import { useQuery, type UseQueryOptions } from '@tanstack/react-query'
import { z } from 'zod'
import { apiPaths } from '@/api/api-paths'
import { queryKeys } from '@/api/query-keys'
import { getApiAuthToken } from '@/components/auth/api-auth-token'

// Schemas - V2 API spec
const organizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  created_at: z.string().optional(),
  external_id: z.string().optional(),
})

// UserResponse: v2 backend returns id, preferred_username, email, roles, claims, organizations (no keycloak_sub)
const userDataResponseSchema = z.object({
  id: z.string(),
  keycloak_sub: z.string().optional(),
  preferred_username: z.string().nullable().optional(),
  email: z.string().email().nullable().optional(),
  roles: z.array(z.string()).optional().default([]),
  claims: z.record(z.unknown()).optional().default({}),
  organizations: z.array(organizationSchema).optional().default([]),
})

const apiResponseSchema = z.object({
  message: z.string(),
  data: userDataResponseSchema,
})

export const authUserSchema = z.object({
  id: z.string().min(1, 'User ID is required'),
  username: z.string().nullable().optional(),
  email: z.string().email().nullable(),
  roles: z.array(z.string()).optional().default([]),
})

export type AuthUser = z.infer<typeof authUserSchema>

async function fetchWithAuthToken(url: string, options: RequestInit = {}): Promise<Response> {
  const token = getApiAuthToken()
  return fetch(url, {
    ...options,
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include',
  })
}

async function fetchMe(): Promise<AuthUser> {
  const res = await fetchWithAuthToken(apiPaths.auth.me())

  if (!res.ok) {
    const text = (await res.text()) || res.statusText
    throw new Error(`Failed to fetch user: ${res.status} ${text}`)
  }

  const jsonData = await res.json()
  const parsedResponse = apiResponseSchema.parse(jsonData)
  const userData = parsedResponse.data

  return authUserSchema.parse({
    id: String(userData.id),
    username: userData.preferred_username ?? null,
    email: userData.email ?? null,
    roles: userData.roles ?? [],
  })
}

export function useAuthMe(options?: Omit<UseQueryOptions<AuthUser>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: queryKeys.auth.me(),
    queryFn: fetchMe,
    ...options,
  })
}
